import React, { useContext, useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { UserContext } from '../../../UserContext'; 
import { PriceContext, defaultPaymentData } from '../Context/PriceContext';
import { useNavigate } from 'react-router-dom'; 
import axiosInstance from '../../../axiosInstance';
import './ConfirmationPayment.css';
import moment from 'moment-timezone';
import { LoadingMessages } from '../../Utilities/LoadingMessages.jsx';
import confetti from 'canvas-confetti';

const API_URL = process.env.REACT_APP_API_URL;
const URL = `${API_URL}/api/track`;

const ConfirmationPayment = () => {
  const [loading, setLoading] = useState(true); 
  const isMounted = useRef(true); 
  const {confirmation, setConfirmation, paymentSuccess, setPaymentSuccess, message, setMessage, paymentData, setPaymentData} = useContext(PriceContext);
  const [coupon, setCoupon] = useState('');
  const [coupons, setCoupons] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [finalTotalCost, setFinalTotalCost] = useState('0.00');
  const [initialCost, setInitialCost] = useState(0);
  const [deliveryCost, setDeliveryCost] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const navigate = useNavigate(); 
  
  const updatePaymentStatus = (status) => {
    setPaymentData((prev) => ({ ...prev, paymentStatus: status }));
  };

  const calculateDeliveryPrice = (initialCost, deliveryTurnaround) => {
    const cost = parseFloat(initialCost);
    let deliveryPrice = 0;
    
    switch (deliveryTurnaround) {
      case 'Same Day (4-6hrs)':
        if (cost < 100) deliveryPrice = 169.46;
        else if (cost >= 100 && cost < 320) deliveryPrice = 232.88;
        else if (cost >= 320 && cost < 499) deliveryPrice = 338.59;
        else if (cost >= 500 && cost < 899) deliveryPrice = 423.16;
        else if (cost >= 900 && cost < 1499) deliveryPrice = 835.45;
        break;
      case 'Next Day (24hrs)':
        deliveryPrice = cost * 0.33;
        break;
      case 'Priority (72 hours)':
        deliveryPrice = cost * 0.06;
        break;
      case 'Standard (5-7 days)':
      default:
        deliveryPrice = 0;
        break;
    }
    
    return deliveryPrice;
  };

  const getDeliveryLabel = (deliveryTurnaround) => {
    switch (deliveryTurnaround) {
      case 'Same Day (4-6hrs)':
        return 'Same-Day Delivery';
      case 'Next Day (24hrs)':
        return 'Next-Day Delivery';
      case 'Priority (72 hours)':
        return 'Priority Delivery';
      case 'Standard (5-7 days)':
        return 'Standard Delivery';
      default:
        return deliveryTurnaround;
    }
  };

  const couponCodes = {
    'DISCOUNT25': 0.25,
    'SAVE10': 0.10,
    'SUMMER15': 0.15,
    'AKU_DISCOUNT_25': 0.25,
    'HORTON_FREE_PROJECT': 200,
  };

  const isCouponExpired = (expirationDate) => {
    const currentDate = moment().tz('America/New_York');
    const expiration = moment.tz(expirationDate, 'America/New_York');
    return currentDate.isAfter(expiration);
  };

  const calculateInitialCost = () => {
    const wordcount = parseInt(paymentData.wordcount?.replace(/,/g, '') || '0');
    if (isNaN(wordcount)) return '0.00';
    
    let cost = 0;
    if (wordcount <= 500) {
      cost += 58.49;
    } else if (wordcount > 500 && wordcount <= 2000) {
      cost += 190.63;
    } else if (wordcount > 2000 && wordcount <= 2500) {
      cost += 291.05;
    } else if (wordcount > 2500 && wordcount <= 4000) {
      cost += 349.19;
    } else if (wordcount > 4000 && wordcount <= 4500) {
      cost += 365.05;
    } else if (wordcount > 4500 && wordcount <= 6000) {
      cost += 423.19;
    } else if (wordcount > 6000 && wordcount <= 6500) {
      cost += 481.33;
    } else if (wordcount > 6500 && wordcount <= 8000) {
      cost += 708.61;
    } else if (wordcount > 8000 && wordcount <= 8500) {
      cost += 766.74;
    } else if (wordcount > 8500 && wordcount <= 10000) {
      cost += 835.45;
    } else {
      cost += 835.45;
      const additionalWords = wordcount - 10000;
      const additionalSets = Math.ceil(additionalWords / 500);
      const additionalCost = additionalSets * 68.71;
      cost += additionalCost;
    }

    if (paymentData.services) {
      for (const [service, isSelected] of Object.entries(paymentData.services)) {
        if (isSelected) {
          switch (service) {
            case 'Consult':
              cost += 720.64;
              break;
            case 'DRR':
              cost += 528.90;
              break;
            case 'Research':
              cost += 328.90;
              break;
            case 'Investigate':
              cost += 169.49;
              break;
            case 'Layout':
              cost += 338.62;
              break;
            case 'Design':
              cost += 232.88;
              break;
            case 'Compress':
              cost += 26.78;
              break;
            case 'Video':
              cost += 42.64;
              break;
            case 'Review':
              cost += 10.93;
              break;
            case 'Audio':
              cost += 10.93;
              break;
            case 'Convert':
              cost += 1.38;
              break;
            case 'Merge':
              cost += 1.38;
              break;
            default:
              break;
          }
        }
      }
    }
    return cost.toFixed(2);
  };

  useEffect(() => {
    const initialize = async () => {
      if (!paymentData || Object.keys(paymentData).length === 0) {
        setLoading(true);
      } else {
        setLoading(false);
      }
    };

    initialize();

    return () => {
      isMounted.current = false;
    };
  }, [paymentData]);

  useEffect(() => {
    if (!paymentData.wordcount || !paymentData.deliveryTurnaround) return;
    const initial = calculateInitialCost();
    setInitialCost(initial);
  
    const deliveryCharge = calculateDeliveryPrice(initial, paymentData.deliveryTurnaround);
    setDeliveryCost(deliveryCharge);
    const totalCost = (parseFloat(initial) + parseFloat(deliveryCharge)).toFixed(2);
    setFinalTotalCost(totalCost);
  }, [paymentData.wordcount, paymentData.deliveryTurnaround]);
  
  useEffect(() => {
    if (couponCodes[coupon.toUpperCase()]) {
      applyCoupon();
    }
  }, [coupon]);
  
  const applyCoupon = () => {
    const discountValue = couponCodes[coupon.toUpperCase()];
    let parsedTotalCost = parseFloat(finalTotalCost);
    if (isNaN(parsedTotalCost)) {
      alert('Error calculating total cost. Please check the values.');
      return;
    }
  
    if (coupons.includes(coupon.toUpperCase())) {
      alert('Coupon already applied.');
      return;
    }
  
    if (coupon.toUpperCase() === 'HORTON_FREE_PROJECT') {
      const expirationDate = '2024-12-22';
      if (isCouponExpired(expirationDate)) {
        alert('Coupon has expired.');
        setDiscount(0);
        return;
      }
      let newTotalCost = Math.max(parsedTotalCost - 200, 0).toFixed(2);
      setDiscount(200);
      setFinalTotalCost(newTotalCost);
      setCoupons([...coupons, coupon.toUpperCase()]);
    } else if (discountValue) {
      let discountedTotalCost = (parsedTotalCost - parsedTotalCost * discountValue).toFixed(2);
      setDiscount(discountValue);
      setFinalTotalCost(discountedTotalCost);
      setCoupons([...coupons, coupon.toUpperCase()]);
    } else {
      alert('Invalid coupon. Please try again.');
      setDiscount(0);
    }
  };
  
  const handleConfirmation = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const data = {
      email: paymentData.email || 'no@Email.com',
      total: finalTotalCost,
      wordCount: paymentData.wordcount,
      deliveryTurnaround: paymentData.deliveryTurnaround,
      deadline: paymentData.deadline,
      documentType: paymentData.documentType,
      additionalServices: Object.keys(paymentData.services).filter(service => paymentData.services[service]),
      couponCode: coupons,
      instructions: paymentData.instructions,
      files:  paymentData.fileNames || [],
      sessionId: sessionStorage.getItem('sessionId') || 'unknown'
    };

    try {
      const response = await axiosInstance.post(URL, data);
      if (response.status === 200) {
        const newOrder = Array.isArray(response.data.newOrder) ? response.data.newOrder : [response.data.newOrder];
        const formattedMessages = newOrder.flatMap(item => {
          const message = item;
          return {
            id: message._id,
            email: message.email,
            total: message.total, 
            wordCount: message.wordCount, 
            deliveryTurnaround: message.deliveryTurnaround,
            deadline: message.deadline,
            documentType: message.documentType,
            services: message.additionalServices || [],
            couponCodes: message.couponCode || [], 
            files: Array.isArray(message.files) ? message.files : [],
            instructions: message.instructions,
            create_time: message.create_time,
          };
        });
      
        setMessage(formattedMessages[0]); 
        setPaymentData(prev => ({
          ...prev,
          amount: finalTotalCost,
          numericAmount: parseFloat(finalTotalCost),
        }));
        setConfirmation(true);
        setShowCelebration(true);
        setTimeout(() => setShowCelebration(false), 3000);
      } else {
        console.log('Error sending data');
      }
    } catch (error) {
      console.error('Error during data submission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (showCelebration) {
      // Enhanced confetti effect
      confetti({
        particleCount: 150,
        spread: 100,
        origin: { y: 0.6 },
        colors: ['#160a3d', '#61dafb', '#a5c5fe', '#ffffff', '#ff0000', '#00ff00', '#0000ff'],
        shapes: ['circle', 'square'],
        startVelocity: 30,
        scalar: 1.2,
      });
      
      // Add a second burst for more effect
      setTimeout(() => {
        confetti({
          particleCount: 100,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ['#160a3d', '#61dafb', '#a5c5fe', '#ffffff'],
        });
      }, 250);
      
      // Add a third burst from the right side
      setTimeout(() => {
        confetti({
          particleCount: 100,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ['#160a3d', '#61dafb', '#a5c5fe', '#ffffff'],
        });
      }, 400);
    }
  }, [showCelebration]);

  const countFiles = () => {
    return message.files?.length || 0;
  };
  
  const handleContinueClick = () => {
    window.history.replaceState(null, "", "/login-signup");
    setMessage();
    setPaymentData(defaultPaymentData);
    setPaymentSuccess(false);
    setConfirmation(false);
    navigate('/login-signup', { replace: true });
  };

  useEffect(() => {
    const handleBackNavigation = () => {
      setMessage(); 
      setPaymentData(defaultPaymentData);
      setPaymentSuccess(false);
      setConfirmation(false);
      
      navigate('/login-signup', { replace: true });
    };
    
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handleBackNavigation);
    return () => {
      window.removeEventListener('popstate', handleBackNavigation);
    };
  }, [navigate, setMessage]);

  if (loading) {
    return (
      <div className="creative-loading">
        <LoadingMessages />
      </div>
    );
  }

  return (
    <section id="confirmation">
      <Helmet>
        <title>Confirmation & Payment - Contranorm Media & Production</title>
      </Helmet>

      <div className="confirmation-container">
        {!paymentSuccess && !confirmation && (
          <div className="order-summary-card">
            <div className="order-header">
              <div className="success-icon">
                <svg viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="45" fill="#4CAF50" />
                  <path d="M30,50 L45,65 L70,35" stroke="white" strokeWidth="5" fill="none" />
                </svg>
              </div>
              <h2>Initial Quote</h2>
            </div>
            
            <div className="cost-breakdown">
              <div className="cost-item">
                <span>Starting Cost:</span>
                <span>${initialCost}</span>
              </div>
              
              {deliveryCost > 0 && (
                <div className="cost-item delivery">
                  <span>{getDeliveryLabel(paymentData.deliveryTurnaround)}:</span>
                  <span>${deliveryCost.toFixed(2)}</span>
                </div>
              )}
              
              {discount > 0 && (
                <div className="cost-item discount">
                  <span>Discount:</span>
                  <span>-${typeof discount === 'number' && discount < 1 ? (parseFloat(initialCost) * discount).toFixed(2) : discount}</span>
                </div>
              )}
              
              <div className="cost-item total">
                <span>Amount Due:</span>
                <span>${finalTotalCost}</span>
              </div>
            </div>

            <div className="coupon-section">
              <div className="coupon-input-group">
                <input 
                  className="coupon-input"
                  type="text"
                  placeholder="Enter coupon code"
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                />
                <button className="apply-coupon" onClick={applyCoupon}>
                  Apply
                </button>
              </div>
              <button
                className="submit-order-btn"
                onClick={() => handleConfirmation(finalTotalCost)}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="loading-spinner"></div>
                ) : (
                  <>
                    <span>Pay Now</span>
                    
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {paymentSuccess && message && (
          <div className="success-notification">
            <div className="notification-header">
              <div className="notification-badge">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" fill="#4CAF50"/>
                </svg>
                <span>ORDER CONFIRMED</span>
              </div>
              <div className="notification-id">#{message.create_time}-0827</div>
            </div>

            <div className="notification-content">
              <h3>Your project is underway! 🚀</h3>
              <p>
                Your <strong>{message.documentType}</strong> is now being processed by our team. 
                Please access your professional dashboard before <strong>{message.deadline}</strong> to review your project.
              </p>
              
              <div className="key-details">
                <div className="detail-card">
                  <div className="detail-icon">📝</div>
                  <div className="detail-content">
                    <span>Document Type</span>
                    <strong>{message.documentType}</strong>
                  </div>
                </div>
                
                <div className="detail-card">
                  <div className="detail-icon">⏰</div>
                  <div className="detail-content">
                    <span>Deadline</span>
                    <strong>{message.deadline}</strong>
                  </div>
                </div>
                
                <div className="detail-card">
                  <div className="detail-icon">📊</div>
                  <div className="detail-content">
                    <span> Current Word Count</span>
                    <strong>{message.wordCount}</strong>
                  </div>
                </div>
                
                <div className="detail-card">
                  <div className="detail-icon">💰</div>
                  <div className="detail-content">
                    <span>Total Cost</span>
                    <strong>${message.total}</strong>
                  </div>
                </div>
              </div>

              <div className="action-notes">
                <h4>Next Steps:</h4>
                <ul>
                  <li>For feedback or revisions, submit at least 24 hours in advance.</li>
                  <li>Use sticky notes to point out any factual inaccuracies, omissions, or errors.</li>
                  <li>Connect with the Project Manager via Live Chat for any project-related questions.</li>
                </ul>
              </div>
            </div>

            <div className="notification-footer">
              <p>
                By continuing, you agree to our <a href="https://compliance.contranormdigital.com/">Terms & Conditions</a>.
              </p>
              <footer>© 2025 Contranorm Media & Production&trade; All Rights Reserved.</footer>
            </div>
            
            <div className="continue-button-container">
              <button className="continue-button" onClick={handleContinueClick}>
                Continue to Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ConfirmationPayment;